import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import WabaCallingAPI from './api';
import { WABA_CALLING_EVENTS } from './constants';
import WebRtcClient from './webRtcClient';

const TERMINAL_STATES = [
  'completed',
  'failed',
  'rejected',
  'no_answer',
  'terminated',
];
const DEFAULT_RINGTONE_URL = '/audio/dashboard/bell.mp3';
const DEFAULT_OUTGOING_RINGTONE_URL = '/audio/dashboard/ringtone.mp3';
const DEFAULT_RINGTONE_VOLUME = 0.5;

const ringtoneUrl = () =>
  window.chatwootConfig?.whatsappWabaCallingRingtoneUrl || DEFAULT_RINGTONE_URL;

const outgoingRingtoneUrl = () =>
  window.chatwootConfig?.whatsappWabaCallingOutgoingRingtoneUrl ||
  DEFAULT_OUTGOING_RINGTONE_URL;
const ringtoneVolume = () => {
  const volume = Number(
    window.chatwootConfig?.whatsappWabaCallingRingtoneVolume
  );
  if (!Number.isFinite(volume)) return DEFAULT_RINGTONE_VOLUME;

  return Math.min(Math.max(volume, 0), 1);
};

const normalize = data => {
  const source = data?.session || data;
  if (!source?.id) return null;

  const state = source.state;
  return {
    id: source.id,
    provider_call_id: source.provider_call_id,
    inbox_id: source.inbox_id,
    contact_id: source.contact_id,
    conversation_id: source.conversation_id,
    direction: source.direction,
    state,
    terminal: Boolean(source.terminal || TERMINAL_STATES.includes(state)),
    agent_id: source.agent_id,
    from_number: source.from_number,
    to_number: source.to_number,
    has_signaling_offer: source.has_signaling_offer,
    updated_at: source.updated_at,
  };
};

export const useWabaCallingStore = defineStore('astrachatWabaCalling', {
  state: () => ({
    sessions: [],
    activeId: null,
    signalingBySession: {},
    incomingRingtone: null,
    outgoingRingtone: null,
    busy: false,
  }),

  getters: {
    active: state =>
      state.sessions.find(session => session.id === state.activeId) || null,
    incoming: state =>
      state.sessions.filter(
        session =>
          !session.terminal &&
          session.id !== state.activeId &&
          session.direction === 'user_initiated' &&
          !session.agent_id
      ),
  },

  actions: {
    upsert(payload) {
      const session = normalize(payload);
      if (!session) return null;

      const index = this.sessions.findIndex(item => item.id === session.id);
      if (index === -1) this.sessions.push(session);
      else this.sessions[index] = { ...this.sessions[index], ...session };
      return session;
    },

    remove(sessionId) {
      if (this.activeId === sessionId) {
        WebRtcClient.close();
        this.activeId = null;
      }
      this.sessions = this.sessions.filter(session => session.id !== sessionId);
      delete this.signalingBySession[sessionId];
      this.syncIncomingRingtone();
      this.syncOutgoingRingtone();
    },

    playIncomingRingtone() {
      if (typeof Audio === 'undefined') return;
      if (!this.incomingRingtone) {
        this.incomingRingtone = markRaw(new Audio(ringtoneUrl()));
        this.incomingRingtone.loop = true;
      }

      this.incomingRingtone.volume = ringtoneVolume();
      this.incomingRingtone.play().catch(() => {});
    },

    stopIncomingRingtone() {
      if (!this.incomingRingtone) return;

      this.incomingRingtone.pause();
      this.incomingRingtone.currentTime = 0;
    },

    playOutgoingRingtone() {
      if (typeof Audio === 'undefined') return;
      if (!this.outgoingRingtone) {
        this.outgoingRingtone = markRaw(new Audio(outgoingRingtoneUrl()));
        this.outgoingRingtone.loop = true;
      }

      this.outgoingRingtone.volume = ringtoneVolume();
      this.outgoingRingtone.play().catch(() => {});
    },

    stopOutgoingRingtone() {
      if (!this.outgoingRingtone) return;

      this.outgoingRingtone.pause();
      this.outgoingRingtone.currentTime = 0;
    },
    syncIncomingRingtone() {
      if (this.incoming.length) this.playIncomingRingtone();
      else this.stopIncomingRingtone();
    },

    syncOutgoingRingtone() {
      if (
        this.active?.direction === 'business_initiated' &&
        !this.active.terminal &&
        this.active.state !== 'in_progress'
      ) {
        this.playOutgoingRingtone();
      } else {
        this.stopOutgoingRingtone();
      }
    },
    async handleRealtime({ event, data }) {
      const session = this.upsert(data);
      if (!session) return;

      if (event === WABA_CALLING_EVENTS.ENDED || session.terminal) {
        this.remove(session.id);
        return;
      }

      if (data?.signaling?.sdp) {
        this.signalingBySession[session.id] = data.signaling;
      }
      this.syncIncomingRingtone();
      this.syncOutgoingRingtone();

      if (
        session.id === this.activeId &&
        session.direction === 'business_initiated'
      ) {
        try {
          await WebRtcClient.applyAnswer(this.signalingBySession[session.id]);
          delete this.signalingBySession[session.id];
        } catch (error) {
          this.remove(session.id);
        }
      }
    },

    async start(inboxId, contactId) {
      if (this.busy || this.active || this.incoming.length) {
        return { error: 'call_in_progress' };
      }

      this.busy = true;
      // This runs in the agent's click handler, allowing browsers to authorize
      // audio before the asynchronous capability and signaling requests begin.
      this.playOutgoingRingtone();
      try {
        const capability = (await WabaCallingAPI.capability(inboxId, contactId))
          .data;
        if (!capability.can_start_call) {
          this.stopOutgoingRingtone();
          return { error: 'missing_call_permission' };
        }

        const signaling = await WebRtcClient.outboundOffer();
        const session = this.upsert(
          (await WabaCallingAPI.connect(inboxId, contactId, signaling)).data
        );
        this.activeId = session.id;
        this.syncOutgoingRingtone();
        return { session };
      } catch (error) {
        WebRtcClient.close();
        this.stopOutgoingRingtone();
        return {
          error: error.response?.data?.error || error.message || 'call_failed',
        };
      } finally {
        this.busy = false;
      }
    },

    async requestPermission(inboxId, contactId) {
      try {
        const data = (
          await WabaCallingAPI.requestPermission(inboxId, contactId)
        ).data;
        return { permission: data.result || 'permission_requested' };
      } catch (error) {
        return {
          error: error.response?.data?.error || 'permission_request_failed',
        };
      }
    },

    async accept(session) {
      if (this.busy || !session) return { error: 'call_unavailable' };

      this.busy = true;
      this.activeId = session.id;
      this.stopIncomingRingtone();
      this.stopOutgoingRingtone();
      try {
        const claim = await WabaCallingAPI.claim(session.inbox_id, session.id);
        const claimed = this.upsert(claim.data);
        const answer = await WebRtcClient.inboundAnswer(claim.data.signaling);
        const accepted = this.upsert(
          (
            await WabaCallingAPI.action(
              claimed.inbox_id,
              claimed.id,
              'accept',
              answer
            )
          ).data
        );
        this.activeId = accepted.id;
        return { session: accepted };
      } catch (error) {
        WebRtcClient.close();
        this.activeId = null;
        this.syncIncomingRingtone();
        return {
          error: error.response?.data?.error || error.message || 'call_failed',
        };
      } finally {
        this.busy = false;
      }
    },

    async end(session, action = 'terminate') {
      if (!session) return;

      this.stopIncomingRingtone();
      await WabaCallingAPI.action(session.inbox_id, session.id, action).catch(
        () => {}
      );
      this.remove(session.id);
    },
  },
});

export { normalize };
